/**
 * AuthGuard · protège les routes privées.
 * - Pendant le boot (refresh silencieux) : affiche un splash.
 * - Si anonyme : redirige sur /login en gardant l'URL d'origine.
 * - Si authentifié : rend les enfants.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "./AuthContext";
import { Logo } from "../ui/Logo";
import { useDemoMode } from "../lib/demo/demoMode";

export function AuthGuard({ children }: { children: ReactNode }) {
  const { status, contractSignatureRequired, mandateRequired } = useAuth();
  const demoActive = useDemoMode();
  const location = useLocation();

  if (status === "loading") {
    return <SplashLoading />;
  }
  if (status === "anonymous") {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  // Mode démo (lecture seule, données fictives) : on lève les gates
  // d'onboarding pour laisser explorer librement l'application.
  if (demoActive) {
    return <>{children}</>;
  }
  // Signature du contrat obligatoire au 1er accès : blocage proactif (ne dépend
  // pas d'un 403 sur une requête de données) → pas de flash du dashboard.
  if (contractSignatureRequired && location.pathname !== "/signature") {
    return <Navigate to="/signature" replace />;
  }
  // Mandat SEPA obligatoire (1er mois payé CB, mandat pas encore posé).
  if (mandateRequired && location.pathname !== "/mandat") {
    return <Navigate to="/mandat" replace />;
  }
  return <>{children}</>;
}

export function GuestGuard({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <SplashLoading />;
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function SplashLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <Logo size={48} />
        <div className="h-1 w-32 overflow-hidden rounded-full bg-navy-100">
          <div className="h-full w-1/2 animate-pulse rounded-full bg-accent-500" />
        </div>
      </div>
    </div>
  );
}
